import { useEffect, useMemo, useRef, useState } from 'react';

const POPOVER_WIDTH = 320;
const VIEWPORT_GAP = 18;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getPopoverPosition(rect, placement = 'bottom', cardHeight = 290) {
  if (!rect) {
    return {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const cardWidth = Math.min(POPOVER_WIDTH, viewportWidth - (VIEWPORT_GAP * 2));
  const maxLeft = Math.max(VIEWPORT_GAP, viewportWidth - cardWidth - VIEWPORT_GAP);
  const centeredLeft = clamp(rect.left + (rect.width / 2) - (cardWidth / 2), VIEWPORT_GAP, maxLeft);

  const verticalPadding = (cardHeight / 2) + VIEWPORT_GAP;

  if (placement === 'left') {
    const nextLeft = rect.left - cardWidth - 20;
    if (nextLeft >= VIEWPORT_GAP) {
      return {
        top: clamp(rect.top + (rect.height / 2), verticalPadding, viewportHeight - verticalPadding),
        left: nextLeft,
        transform: 'translateY(-50%)',
      };
    }
  }

  if (placement === 'right') {
    const nextLeft = rect.right + 20;
    if (nextLeft + cardWidth <= viewportWidth - VIEWPORT_GAP) {
      return {
        top: clamp(rect.top + (rect.height / 2), verticalPadding, viewportHeight - verticalPadding),
        left: nextLeft,
        transform: 'translateY(-50%)',
      };
    }
  }

  if (placement === 'top') {
    const nextTop = rect.top - 18;
    if (nextTop - cardHeight >= VIEWPORT_GAP) {
      return {
        top: nextTop,
        left: centeredLeft,
        transform: 'translateY(-100%)',
      };
    }
  }

  const belowTop = rect.bottom + 20;
  if (belowTop + cardHeight <= viewportHeight - VIEWPORT_GAP) {
    return {
      top: belowTop,
      left: centeredLeft,
      transform: 'none',
    };
  }

  const topPlacement = rect.top - 18;
  if (topPlacement - cardHeight >= VIEWPORT_GAP) {
    return {
      top: topPlacement,
      left: centeredLeft,
      transform: 'translateY(-100%)',
    };
  }

  return {
    top: clamp(viewportHeight - cardHeight - VIEWPORT_GAP, VIEWPORT_GAP, viewportHeight - VIEWPORT_GAP),
    left: centeredLeft,
    transform: 'none',
  };
}

export default function GuidedTour({
  open,
  steps,
  onClose,
  onComplete,
  isMobile: isMobileProp,
  onStepChange,
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [cardHeight, setCardHeight] = useState(250);
  const cardRef = useRef(null);

  const safeSteps = useMemo(() => (Array.isArray(steps) ? steps.filter(Boolean) : []), [steps]);
  const currentStep = safeSteps[stepIndex] || null;

  useEffect(() => {
    if (!open) {
      setStepIndex(0);
      setTargetRect(null);
      return;
    }

    setStepIndex(0);
  }, [open, safeSteps.length]);

  useEffect(() => {
    if (!open || !currentStep) {
      onStepChange?.(null);
      return;
    }

    onStepChange?.(currentStep);
  }, [currentStep, onStepChange, open]);

  useEffect(() => {
    if (!open || !currentStep) return undefined;

    let frameId = null;
    let scrolledToTarget = false;

    const updatePosition = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        const element = currentStep.selector ? document.querySelector(currentStep.selector) : null;

        if (element && !scrolledToTarget) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: currentStep.scrollBlock || 'nearest',
            inline: 'nearest',
          });
          scrolledToTarget = true;
        }

        setTargetRect(element ? element.getBoundingClientRect() : null);
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [currentStep, open]);

  useEffect(() => {
    if (!open) return undefined;

    let frameId = null;

    const updateCardHeight = () => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        if (cardRef.current) {
          const nextHeight = Math.ceil(cardRef.current.getBoundingClientRect().height);
          if (nextHeight > 0) {
            setCardHeight(nextHeight);
          }
        }
      });
    };

    updateCardHeight();
    window.addEventListener('resize', updateCardHeight);

    return () => {
      window.removeEventListener('resize', updateCardHeight);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [currentStep, open, stepIndex]);

  if (!open || !currentStep || safeSteps.length === 0) {
    return null;
  }

  const popoverPosition = getPopoverPosition(targetRect, currentStep.placement, cardHeight);
  const progressPercent = safeSteps.length > 0 ? ((stepIndex + 1) / safeSteps.length) * 100 : 0;
  const isNarrow = isMobileProp ?? (typeof window !== 'undefined' ? window.innerWidth < 640 : false);

  const handleNext = () => {
    if (stepIndex >= safeSteps.length - 1) {
      onComplete?.();
      return;
    }

    setStepIndex((value) => value + 1);
  };

  const handleBack = () => {
    setStepIndex((value) => Math.max(0, value - 1));
  };

  return (
    <div style={styles.root}>
      <div style={styles.backdrop} onClick={onClose} />

      {targetRect ? (
        <div
          style={{
            ...styles.highlight,
            top: Math.max(8, targetRect.top - 8),
            left: Math.max(8, targetRect.left - 8),
            width: Math.max(0, targetRect.width + 16),
            height: Math.max(0, targetRect.height + 16),
          }}
        />
      ) : null}

      <div
        ref={cardRef}
        style={{
          ...styles.card,
          ...(isNarrow ? styles.cardCompact : null),
          top: popoverPosition.top,
          left: popoverPosition.left,
          transform: popoverPosition.transform,
        }}
      >
        <div style={styles.cardGlow} />
        <div style={styles.cardNoise} />

        <div style={styles.progressRow}>
          <div style={styles.stepPill}>Step {stepIndex + 1} of {safeSteps.length}</div>
          <button type="button" onClick={onClose} style={styles.skipBtn}>Skip tour</button>
        </div>

        <div style={styles.heroWrap}>
          <div style={styles.heroBadge}>Workspace Tour</div>
          <div style={{ ...styles.title, ...(isNarrow ? styles.titleCompact : null) }}>{currentStep.title}</div>
          <div style={{ ...styles.body, ...(isNarrow ? styles.bodyCompact : null) }}>{currentStep.description}</div>
        </div>

        <div style={styles.progressBlock}>
          <div style={styles.progressMeta}>
            <span style={styles.progressLabel}>Tour progress</span>
            <span style={styles.progressCount}>{Math.round(progressPercent)}%</span>
          </div>
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${progressPercent}%` }} />
          </div>
          <div style={styles.dotRow}>
            {safeSteps.map((step, index) => (
              <span
                key={`${step.title}-${index}`}
                style={{
                  ...styles.dot,
                  ...(index === stepIndex ? styles.dotActive : null),
                  ...(index < stepIndex ? styles.dotCompleted : null),
                }}
              />
            ))}
          </div>
        </div>

        <div style={{ ...styles.footer, ...(isNarrow ? styles.footerCompact : null) }}>
          <button type="button" onClick={handleBack} disabled={stepIndex === 0} style={{ ...styles.actionBtn, ...(isNarrow ? styles.actionBtnCompact : null), ...(stepIndex === 0 ? styles.actionBtnDisabled : null) }}>
            Back
          </button>
          <button type="button" onClick={handleNext} style={{ ...styles.actionBtn, ...(isNarrow ? styles.actionBtnCompact : null), ...styles.primaryBtn }}>
            {stepIndex === safeSteps.length - 1 ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: {
    position: 'fixed',
    inset: 0,
    zIndex: 220,
    pointerEvents: 'none',
  },
  backdrop: {
    position: 'absolute',
    inset: 0,
    background: 'transparent',
    pointerEvents: 'auto',
  },
  highlight: {
    position: 'fixed',
    borderRadius: 16,
    border: '2px solid var(--sidebar-accent)',
    background: 'transparent',
    boxShadow: '0 0 0 9999px rgba(7, 12, 18, 0.5), 0 0 0 1px var(--sidebar-accent-glow), 0 12px 30px rgba(0,0,0,0.16)',
    zIndex: 221,
    pointerEvents: 'none',
  },
  card: {
    position: 'fixed',
    width: `min(${POPOVER_WIDTH}px, calc(100vw - 40px))`,
    maxHeight: 'calc(100vh - 24px)',
    borderRadius: 20,
    padding: '12px 14px 11px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-strong)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
    pointerEvents: 'auto', zIndex: 225,
    overflow: 'hidden',
    overflowY: 'auto',
  },
  cardCompact: {
    width: 'min(220px, calc(100vw - 40px))',
    maxHeight: 'calc(100vh - 60px)',
    borderRadius: 14,
    padding: '6px 10px 6px',
  },
  cardGlow: {
    position: 'absolute',
    top: -86,
    right: -52,
    width: 188,
    height: 188,
    borderRadius: 999,
    background: 'radial-gradient(circle, var(--sidebar-accent-glow) 0%, transparent 72%)',
    opacity: 0.9,
    pointerEvents: 'none',
  },
  cardNoise: {
    position: 'absolute',
    inset: 0,
    backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 42%, rgba(255,255,255,0.02) 100%)',
    pointerEvents: 'none',
  },
  progressRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  stepPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 7px',
    borderRadius: 999,
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: 'var(--brand)',
    background: 'var(--bg-elev)',
    border: '1px solid var(--border)',
  },
  skipBtn: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--text-muted)',
  },
  heroWrap: {
    position: 'relative',
    marginTop: 6,
    padding: '6px 8px 6px',
    borderRadius: 14,
    background: 'var(--bg-elev)',
    border: '1px solid var(--border)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 7px',
    borderRadius: 999,
    fontSize: 8,
    fontWeight: 800,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: 'var(--brand-contrast)',
    background: 'var(--brand)',
    border: '1px solid var(--brand)',
  },
  title: {
    marginTop: 6,
    fontSize: 13,
    fontWeight: 800,
    color: 'var(--text-primary)',
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  titleCompact: {
    fontSize: 11,
    marginTop: 4,
  },
  body: {
    marginTop: 4,
    fontSize: 11,
    lineHeight: 1.42,
    color: 'var(--text-muted)',
  },
  bodyCompact: {
    fontSize: 9,
    lineHeight: 1.4,
  },
  progressBlock: {
    marginTop: 6,
    padding: '6px 7px',
    borderRadius: 10,
    background: 'var(--bg-elev)',
    border: '1px solid var(--border)',
  },
  progressMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  progressLabel: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.75,
    textTransform: 'uppercase',
    color: 'var(--text-muted)',
  },
  progressCount: {
    fontSize: 10,
    fontWeight: 800,
    color: 'var(--text-primary)',
  },
  progressTrack: {
    marginTop: 5,
    height: 4,
    width: '100%',
    borderRadius: 999,
    overflow: 'hidden',
    background: 'var(--border)',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)',
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, var(--brand) 0%, var(--sidebar-accent) 100%)',
    boxShadow: '0 8px 18px var(--sidebar-accent-glow)',
    transition: 'width 0.24s ease',
  },
  dotRow: {
    marginTop: 5,
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    flexWrap: 'wrap',
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: 999,
    background: 'var(--border-strong)',
    transition: 'transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease',
  },
  dotActive: {
    width: 12,
    background: 'linear-gradient(90deg, var(--brand) 0%, var(--sidebar-accent) 100%)',
    boxShadow: '0 8px 16px var(--sidebar-accent-glow)',
  },
  dotCompleted: {
    background: 'var(--sidebar-accent)',
  },
  footer: {
    marginTop: 6,
    display: 'flex',
    justifyContent: 'space-between',
    gap: 6,
  },
  footerCompact: {
    marginTop: 6,
    gap: 6,
  },
  actionBtn: {
    minHeight: 32,
    minWidth: 70,
    borderRadius: 12,
    padding: '0 11px',
    border: '1px solid var(--border-strong)',
    background: 'var(--bg-elev)',
    color: 'var(--text-primary)',
    fontSize: 11,
    fontWeight: 800,
    cursor: 'pointer',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
  },
  actionBtnCompact: {
    minHeight: 30,
    minWidth: 0,
    flex: 1,
    padding: '0 8px',
    fontSize: 10,
    borderRadius: 10,
  },
  actionBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  primaryBtn: {
    background: 'linear-gradient(135deg, var(--brand) 0%, var(--sidebar-accent) 100%)',
    color: 'var(--brand-contrast)',
    border: '1px solid var(--sidebar-accent)',
    boxShadow: '0 10px 24px var(--sidebar-accent-glow)',
  },
};
